import { useId, type MouseEvent, type ReactNode } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { Check, GripVertical } from "lucide-react";
import { cn } from "@repo/utils";

export type QsortItem = {
  code: string;
  label: string;
  description?: string;
};

/**
 * `deplace` : l'item vient d'être renvoyé par un échange, sans qu'on l'ait
 * touché — on le signale pour qu'il ne passe pas pour perdu.
 * `remplacable` : son niveau est plein et une sélection attend qu'on choisisse
 * lequel de ses occupants elle remplace.
 */
export type VarianteChip = "normal" | "deplace" | "remplacable";

export const ID_RESERVE = "reserve";

/**
 * Anneau de focus clavier des contrôles du tableau. Le défaut du navigateur
 * (contour 1 px à 50 % d'opacité) se perdait sur les bordures des cartes.
 */
export const anneauFocus =
  "outline-none focus-visible:ring-2 focus-visible:ring-charte-accent focus-visible:ring-offset-2 focus-visible:ring-offset-card";

/**
 * Nom accessible d'un niveau : son libellé d'abord (c'est ce qu'on cherche en
 * parcourant), puis son remplissage, puis ce qu'une activation ferait.
 */
export function nomAccessibleNiveau(
  libelle: string,
  occupees: number,
  capacite: number,
  selection: string | undefined,
): string {
  const remplissage = `${libelle}, ${occupees} sur ${capacite}`;
  return selection ? `${remplissage} — placer «\u00a0${selection}\u00a0» ici` : remplissage;
}
export const prefixeNiveau = "niveau:";
export const prefixeItem = "item:";

/**
 * Forme commune d'un item — dans la réserve, dans un niveau et sous le curseur
 * pendant un drag : les trois doivent se reconnaître comme le même objet.
 */
const formeChip =
  "flex w-full items-start rounded-lg border bg-card text-left text-sm leading-snug text-card-foreground shadow-xs";

export function Chip({
  item,
  actif,
  variante = "normal",
  lectureSeule,
  onTap,
  poignee,
  dragProps,
}: {
  item: QsortItem;
  actif: boolean;
  variante?: VarianteChip;
  lectureSeule: boolean;
  onTap: () => void;
  /** Poignée visible : seulement dans la réserve, où la place ne manque pas. */
  poignee: boolean;
  /** Listeners dnd-kit : posés sur TOUTE la carte, pas seulement la poignée. */
  dragProps?: Record<string, unknown>;
}) {
  const idDescription = useId();
  return (
    <div
      className={cn(
        formeChip,
        "transition-colors",
        actif
          ? "border-charte-accent bg-charte-hover-opaque ring-2 ring-charte-accent/30"
          : variante === "deplace"
            ? "border-charte-line-accent-strong bg-charte-wash-accent"
            : variante === "remplacable"
              ? "cursor-pointer border-dashed border-charte-accent bg-charte-hover-opaque"
              : "border-charte-line-strong",
        !lectureSeule && !actif && "hover:border-charte-line-accent hover:bg-charte-hover",
        dragProps && "cursor-grab active:cursor-grabbing",
        // L'anneau de focus entoure toute la carte, poignée comprise.
        "has-[button:focus-visible]:ring-2 has-[button:focus-visible]:ring-charte-accent has-[button:focus-visible]:ring-offset-2 has-[button:focus-visible]:ring-offset-card",
      )}
      style={dragProps ? { touchAction: "none" } : undefined}
      {...dragProps}
    >
      {poignee && dragProps && (
        <span className="shrink-0 py-2.5 pl-3 text-charte-ghost" aria-hidden>
          <GripVertical className="size-4" />
        </span>
      )}
      {lectureSeule ? (
        // En lecture seule, un bouton désactivé serait sauté par la tabulation
        // ET annoncé comme « indisponible » : du texte se lit mieux.
        <span className="flex-1 px-3 py-2">{item.label}</span>
      ) : (
        <button
          type="button"
          onClick={(e: MouseEvent) => {
            // La colonne est elle-même cliquable : sans ça, taper un item placé
            // déclencherait aussi le placement dans sa colonne.
            e.stopPropagation();
            onTap();
          }}
          title={item.description}
          aria-pressed={actif}
          aria-describedby={item.description ? idDescription : undefined}
          // Le bouton couvre toute la carte : au doigt, c'est la carte qu'on vise,
          // pas son texte (44 px de haut minimum sous `md`).
          className="min-h-11 flex-1 cursor-[inherit] rounded-[inherit] px-3 py-2 text-left outline-none md:min-h-0"
        >
          {item.label}
        </button>
      )}
      {item.description && (
        <span id={idDescription} className="sr-only">
          {item.description}
        </span>
      )}
    </div>
  );
}

/** Aperçu sous le curseur pendant un drag : même forme que la chip posée. */
export function ChipEnVol({ item }: { item: QsortItem }) {
  return (
    <div
      className={cn(
        formeChip,
        "cursor-grabbing border-charte-accent px-3 py-2 shadow-lg ring-2 ring-charte-accent/30",
      )}
    >
      {item.label}
    </div>
  );
}

export function ChipDraggable(props: {
  item: QsortItem;
  actif: boolean;
  variante?: VarianteChip;
  lectureSeule: boolean;
  dnd: boolean;
  poignee: boolean;
  onTap: () => void;
}) {
  const { listeners, setNodeRef, isDragging } = useDraggable({
    id: `${prefixeItem}${props.item.code}`,
    disabled: props.lectureSeule || !props.dnd,
  });
  // Un item est aussi une CIBLE de drop : déposer sur lui désigne explicitement
  // l'item avec lequel échanger quand le niveau est plein (§7.1).
  const { setNodeRef: setDropRef } = useDroppable({
    id: `${prefixeItem}${props.item.code}`,
    disabled: props.lectureSeule || !props.dnd,
  });

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        setDropRef(node);
      }}
      style={{ opacity: isDragging ? 0.4 : 1 }}
    >
      <Chip
        item={props.item}
        actif={props.actif}
        variante={props.variante}
        lectureSeule={props.lectureSeule}
        poignee={props.poignee}
        onTap={props.onTap}
        dragProps={props.dnd ? listeners : undefined}
      />
    </div>
  );
}

export function Zone({
  id,
  children,
  className,
  classeSurvol = "bg-charte-hover",
  onClick,
  role,
  ariaLabelledby,
}: {
  id: string;
  children: ReactNode;
  className?: string;
  classeSurvol?: string;
  onClick?: () => void;
  role?: "group" | "region";
  ariaLabelledby?: string;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      role={role}
      aria-labelledby={ariaLabelledby}
      onClick={onClick}
      className={cn(className, isOver && classeSurvol)}
    >
      {children}
    </div>
  );
}

/**
 * Une colonne de la pyramide : l'en-tête (libellé + remplissage), puis autant
 * d'emplacements que la capacité du niveau. Les emplacements vides sont
 * dessinés — c'est ce qui fait apparaître la silhouette 2-3-4-5-4-3-2 et dit
 * « il reste une place » sans avoir à lire le compteur.
 */
export function ColonneNiveau({
  niveau,
  libelle,
  capacite,
  intensite,
  items,
  selection,
  libelleSelection,
  varianteDe,
  lectureSeule,
  dnd,
  onTapNiveau,
  onTapItem,
}: {
  niveau: number;
  libelle: string;
  capacite: number;
  /** 1 = niveau le plus fort, 0 = le plus faible : dose le trait d'importance. */
  intensite: number;
  items: QsortItem[];
  selection: string | null;
  /** Libellé de l'item sélectionné, pour nommer ce que ferait l'activation. */
  libelleSelection: string | undefined;
  varianteDe: (code: string) => VarianteChip;
  lectureSeule: boolean;
  dnd: boolean;
  /** `parClavier` : activation au clavier (Entrée/Espace), qui déplace le focus. */
  onTapNiveau: (parClavier: boolean) => void;
  onTapItem: (code: string) => void;
}) {
  const complet = items.length === capacite;
  const selectionIci = selection !== null && items.some((i) => i.code === selection);
  // Une sélection venue d'ailleurs fait de chaque colonne une cible : on le
  // montre, sans quoi « cliquez un niveau » ne dit pas OÙ cliquer.
  const cible = selection !== null && !selectionIci && !lectureSeule;
  const vides = Math.max(capacite - items.length, 0);
  const idLibelle = useId();

  return (
    <Zone
      id={`${prefixeNiveau}${niveau}`}
      role="group"
      ariaLabelledby={idLibelle}
      onClick={cible ? () => onTapNiveau(false) : undefined}
      classeSurvol="bg-charte-hover ring-2 ring-charte-line-accent"
      className={cn(
        "flex min-w-0 flex-col gap-2 rounded-lg p-1.5 transition-colors",
        cible && "cursor-pointer hover:bg-charte-hover",
      )}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          // Un clic déclenché au clavier a `detail === 0`.
          onTapNiveau(e.detail === 0);
        }}
        // `aria-disabled` et non `disabled` : le niveau reste atteignable au
        // clavier et lisible au lecteur d'écran avant toute sélection — sinon on
        // ne découvre la structure de la pyramide qu'après avoir choisi un item.
        aria-disabled={selection === null || selectionIci}
        aria-label={nomAccessibleNiveau(libelle, items.length, capacite, cible ? libelleSelection : undefined)}
        className={cn(
          "flex flex-col gap-1 rounded-md px-1 text-left aria-disabled:cursor-default",
          anneauFocus,
        )}
      >
        <span
          aria-hidden
          className="h-1 w-full rounded-full bg-charte-accent"
          style={{ opacity: 0.15 + 0.85 * intensite }}
        />
        {/* Deux lignes réservées : un libellé qui passe à la ligne (« Assez peu
            important » à 1024 px) ne doit pas décaler les emplacements de SA
            colonne par rapport aux autres. */}
        <span
          id={idLibelle}
          className="min-h-[2lh] text-sm font-semibold leading-tight text-foreground"
        >
          {libelle}
        </span>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs tabular-nums",
            complet ? "font-semibold text-charte-accent-light" : "text-muted-foreground",
          )}
        >
          {complet && <Check className="size-3.5" aria-hidden />}
          {items.length}/{capacite}
        </span>
      </button>
      <div className="flex flex-col gap-1.5">
        {items.map((item) => (
          <ChipDraggable
            key={item.code}
            item={item}
            actif={selection === item.code}
            variante={varianteDe(item.code)}
            lectureSeule={lectureSeule}
            dnd={dnd}
            poignee={false}
            onTap={() => onTapItem(item.code)}
          />
        ))}
        {Array.from({ length: vides }, (_, i) => (
          <div
            key={`vide-${i}`}
            aria-hidden
            className={cn(
              "h-10 rounded-lg border border-dashed transition-colors",
              cible
                ? "border-charte-line-accent-strong bg-charte-hover"
                : "border-charte-line-loud",
            )}
          />
        ))}
      </div>
    </Zone>
  );
}
